import {FC, useRef, useEffect, useState} from 'react'
import {shallowEqual, useSelector, connect, useDispatch, ConnectedProps} from 'react-redux'
import * as auth from './AuthRedux'
import {getUserByToken} from './AuthCRUD'
import { LayoutSplashScreen } from '@/__core/layout/SplashScreen'
import { RootState } from '@/setup'
import { CompanyData, getSelectedCompany } from '../helpers/CompanyStorage'
import { hydrateAuthCompanies, processCompanySelection } from '../utils/authUtils'
import { normalizeProfileForAuth } from '../utils/avatarUtils'
import { getStoredAccessToken } from '../helpers/TokenStorage'

const mapState = (state: RootState) => ({auth: state.auth})
const connector = connect(mapState, auth.actions)
type PropsFromRedux = ConnectedProps<typeof connector> & {
  children?: React.ReactNode
}

const AuthInit: FC<PropsFromRedux> = (props) => {
  const {
    children,
    login,
    logout,
    fulfillUser,
    setSelectedCompany,
  } = props
  const didRequest = useRef(false)
  const requestCount = useRef(0)
  const hasProcessedCompany = useRef(false)
  const hasInitiated = useRef(false)
  const dispatch = useDispatch()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const [hasCheckedBootstrapToken, setHasCheckedBootstrapToken] = useState(false)
  const accessToken = useSelector<RootState>(({auth}) => auth.accessToken, shallowEqual)
  const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual)
  const selectedCompany = useSelector<RootState>(({auth}) => auth.selectedCompany, shallowEqual)
  const companies = useSelector(
    (state: RootState) => (state.auth.companies ?? []) as CompanyData[],
    shallowEqual
  )

  // Optimized: Load company from storage IMMEDIATELY on mount if we have user and accessToken
  useEffect(() => {
    if (accessToken && user && !selectedCompany && !hasInitiated.current) {
      const storedCompany = getSelectedCompany()
      if (storedCompany) {
        dispatch(setSelectedCompany(storedCompany))
        hasInitiated.current = true;
        setShowSplashScreen(false);
      }
    }
  }, [accessToken, user, selectedCompany, dispatch, setSelectedCompany]); // Re-check when these values change

  // Auto login chỉ chạy một lần khi component mount
  useEffect(() => {
    const checkAutoLogin = () => {
      const bootstrapToken = getStoredAccessToken();

      if (bootstrapToken && !accessToken) {
        dispatch(login(bootstrapToken));
        setHasCheckedBootstrapToken(true);
        return;
      }

      // No token to bootstrap with: allow app to render public routes.
      setHasCheckedBootstrapToken(true);
      setShowSplashScreen(false);
    };

    // Chỉ check auto login nếu chưa có accessToken và chưa request user
    if (!accessToken && !didRequest.current) {
      checkAutoLogin();
    }
  }, [accessToken, dispatch, login]); // Chỉ chạy một lần khi mount

  // We should request user by authToken before rendering the application
  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!didRequest.current && requestCount.current < 3) {
          requestCount.current += 1
          
          const {data: user} = await getUserByToken()
          dispatch(
            fulfillUser(
              normalizeProfileForAuth(user as unknown as Record<string, unknown>) as unknown as typeof user
            )
          )
          
          // Load selected company from storage if available
          const storedCompany = getSelectedCompany()
          if (storedCompany) {
            dispatch(setSelectedCompany(storedCompany))
            // Mark as initiated so we don't process company again
            hasInitiated.current = true;
          }

          // User bootstrap completed successfully.
          setShowSplashScreen(false)
        }
      } catch (error) {
        if (!didRequest.current) {
          const status = (error as any)?.response?.status

          // Only clear auth context for real authentication failures.
          // Network/server errors on refresh should not force user back to login.
          if (status === 401 || status === 403) {
            dispatch(logout())
          }

          didRequest.current = true
          setShowSplashScreen(false)
        }
      } finally {
        if (requestCount.current >= 2) {
          didRequest.current = true
        }
      }
    }

    if (accessToken && !didRequest.current) {
      requestUser()
    }
  }, [accessToken, dispatch, fulfillUser, logout, setSelectedCompany]) // Chỉ phụ thuộc vào accessToken

  // Process company selection if user is loaded but no selectedCompany
  useEffect(() => {
    const processCompany = async () => {
      // Chỉ xử lý nếu:
      // 1. User đã load
      // 2. Chưa có selectedCompany trong Redux
      // 3. Chưa process company lần nào
      if (user && !selectedCompany && !hasProcessedCompany.current && accessToken && !hasInitiated.current) {
        hasInitiated.current = true;
        hasProcessedCompany.current = true;
        
        try {
          // Check storage one more time in case it was updated
          const storedCompany = getSelectedCompany();
          if (storedCompany) {
            dispatch(setSelectedCompany(storedCompany));
            setShowSplashScreen(false);
            return;
          }
          
          // No company in storage, need to process company selection
          // Determine remember flag based on token storage
          const rememberToken = localStorage.getItem('rememberToken');
          const remember = !!rememberToken;
          
          await processCompanySelection(dispatch, remember);
          
          // Note: For multiple companies or no company scenarios,
          // the user will need to manually select company via Login component
          // This AuthInit just handles the auto-login scenarios
          
          // Hide splash screen after company selection is processed
          setShowSplashScreen(false);
          
        } catch (error) {
          console.error('Error processing company selection in AuthInit:', error);
          setShowSplashScreen(false);
        }
      }
    };

    processCompany();
  }, [user, selectedCompany, accessToken, dispatch, setSelectedCompany]);

  // Safety: when bootstrap has finished and there is still no token, render auth routes.
  useEffect(() => {
    if (hasCheckedBootstrapToken && !accessToken) {
      setShowSplashScreen(false);
    }
  }, [hasCheckedBootstrapToken, accessToken]);

  // After reload / company switch: restore companies list so "Chọn công ty" stays available.
  useEffect(() => {
    if (!accessToken || !user || companies.length > 0) {
      return;
    }

    void hydrateAuthCompanies(dispatch).catch((error) => {
      console.error('Error hydrating auth companies:', error);
    });
  }, [accessToken, user, companies.length, dispatch]);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export default connector(AuthInit)
